import { useEffect, useState } from "react";
import {
  APPLICATION_RESULT_REFERRAL_FLAG_KEY,
  APPLICATION_RESULT_REFERRAL_TEST_VARIANT,
  isApplicationResultReferralEligible,
} from "src/features/application-result-referral/experiment";
import {
  ExperimentVariant,
  useExperimentVariantState,
} from "src/hooks/useInstrumentation";
import { useCurrentUser } from "src/SessionBoundary";

export type ApplicationResultReferralAssignment = {
  /** Always `control` for students outside the experiment. */
  variant: ExperimentVariant;
  /**
   * False only while an eligible student's flag is still resolving. Callers
   * hold their render until this is true so the screen never paints one arm
   * and then swaps to the other.
   */
  resolved: boolean;
};

/**
 * Arm for the `application-result-referral-ask` experiment. See
 * `src/features/application-result-referral/experiment.ts`.
 *
 * `active` is the caller's own condition (e.g. "this student is on hold and
 * on the success plan intro"). Exposure fires only when the student is both
 * eligible and `active`, so the flag is never evaluated for anyone the
 * experiment doesn't target, in-custody students included.
 *
 * The first resolved arm is LATCHED for the life of the component, and the
 * flag subscription is dropped as soon as it latches. A `/flags` answer that
 * lands after the shared hook's 3s control fallback would otherwise record a
 * `test` exposure for a student who was shown control.
 */
export default function useApplicationResultReferralVariant(
  active: boolean
): ApplicationResultReferralAssignment {
  const user = useCurrentUser();
  const eligible = active && isApplicationResultReferralEligible(user);
  const [latched, setLatched] = useState<ExperimentVariant | null>(null);

  const { variant, ready } = useExperimentVariantState({
    flagKey: APPLICATION_RESULT_REFERRAL_FLAG_KEY,
    testValue: APPLICATION_RESULT_REFERRAL_TEST_VARIANT,
    enabled: eligible && latched === null,
  });

  useEffect(() => {
    if (latched !== null || !eligible || !ready) return;
    setLatched(variant);
  }, [latched, eligible, ready, variant]);

  if (!eligible) return { variant: "control", resolved: true };
  return latched === null
    ? { variant: "control", resolved: false }
    : { variant: latched, resolved: true };
}
